import { QUESTION_TYPE } from '@pcty/question-builder-base';
import { isNullOrEmptyString, isGuid, decodeText, isNumberNullOrNaN } from './utilities';
import uuidv4 from 'uuid/v4';


const citrusSurveyJsQuestionTypeMappings = [
  { citrusQuestionTypeId: QUESTION_TYPE.multipleChoice, surveyJsType: 'radiogroup' },
  { citrusQuestionTypeId: QUESTION_TYPE.trueFalse, surveyJsType: 'boolean' },
  { citrusQuestionTypeId: QUESTION_TYPE.checkbox, surveyJsType: 'checkbox' },
  { citrusQuestionTypeId: QUESTION_TYPE.stackRank, surveyJsType: 'stackrank' },
  { citrusQuestionTypeId: QUESTION_TYPE.title, surveyJsType: 'sectionheader' },
  { citrusQuestionTypeId: QUESTION_TYPE.paragraph, surveyJsType: 'paragraph' },
  { citrusQuestionTypeId: QUESTION_TYPE.textField, surveyJsType: 'text' },
  { citrusQuestionTypeId: QUESTION_TYPE.textField, surveyJsType: 'comment' },
  { citrusQuestionTypeId: QUESTION_TYPE.starRating, surveyJsType: 'starrating' },
  { citrusQuestionTypeId: QUESTION_TYPE.ratingScale, surveyJsType: 'rating' },
  { citrusQuestionTypeId: QUESTION_TYPE.multipleText, surveyJsType: 'multilinetext' },
  { citrusQuestionTypeId: QUESTION_TYPE.multipleText, surveyJsType: 'multipletext' },
  { citrusQuestionTypeId: QUESTION_TYPE.constantSum, surveyJsType: 'constantsum' },
  { citrusQuestionTypeId: QUESTION_TYPE.dropdownList, surveyJsType: 'dropdown' },
  { citrusQuestionTypeId: QUESTION_TYPE.video, surveyJsType: 'video' },
];

const citrusSurveyJsSortOrderMappings = [
  { citrusSort: 'defined', citrusLabel:'As Defined', surveyJsSort: '' },
  { citrusSort: 'ascending', citrusLabel:'Ascending', surveyJsSort: 'asc' },
  { citrusSort: 'descending', citrusLabel:'Descending', surveyJsSort: 'desc' },
  { citrusSort: 'random', citrusLabel:'Random', surveyJsSort: 'random' },
];

const otherDefaultCaption = 'Other (describe)';
const dummyVideoToken = 'abdc12345';
const dummyVideoInsertionMethod = 'unknown'

export const convertCitrusToSurveyjsJsonConfiguration = (citrusJson) => {
  // citrusJson var is actually an array of pages with elements, so just grab the first one (for now!)
  let srcJson = (typeof citrusJson == 'string') ? JSON.parse(citrusJson) : citrusJson;
  if (!srcJson || !srcJson[0]) return undefined;
  //console.log(srcJson);

  let pages = srcJson.map((page, index) => {
    // convert all the elements on the page
    let elements = page.map(question => {
      return convertCitrusToSurveyjsJsonQuestion(question);
    });
    // now filter out any null items
    elements = elements.filter((item) => !!item);

    // setup the page obj
    let fixedPage = { name: `page${index+1}`  };
    fixedPage.elements = elements;
    return fixedPage;
  });

  let outputJson = {"pages": pages};
  return outputJson;
};

const questionTypeHasOptions = (citrusTypeId) => {
  return ([
    QUESTION_TYPE.multipleChoice,
    QUESTION_TYPE.multipleText,
    QUESTION_TYPE.dropdownList,
    QUESTION_TYPE.constantSum,
    QUESTION_TYPE.checkbox,
    QUESTION_TYPE.stackRank,
    QUESTION_TYPE.ratingScale].indexOf(citrusTypeId) >= 0);
};

const questionTypeHasMultipleKeyValues = (citrusTypeId) => {
  return ([
    QUESTION_TYPE.constantSum,
    QUESTION_TYPE.multipleText].indexOf(citrusTypeId) >= 0);
};

const questionTypeHasMultipleKeys = (citrusTypeId) => {
  return ([
    QUESTION_TYPE.checkbox,
    QUESTION_TYPE.stackRank].indexOf(citrusTypeId) >= 0);
};

const questionTypeIsStarRatings = (citrusTypeId) => {
  return (citrusTypeId === QUESTION_TYPE.starRating);
};

const questionHasComments = (questionType, citrusOrSurveyObj) => {
  const citrusTypeId = questionType;
  return ([
    QUESTION_TYPE.starRating,
    QUESTION_TYPE.ratingScale].indexOf(citrusTypeId) >= 0 &&
    'allowComments' in citrusOrSurveyObj &&
    citrusOrSurveyObj.allowComments === true)
};

const convertCitrusToSurveyjsJsonQuestion = (citrusQuestion) => {
  if (!citrusQuestion) return undefined;

  let questionType = citrusSurveyJsQuestionTypeMappings.find(x => x.citrusQuestionTypeId === citrusQuestion.typeId);
  if (!questionType || !citrusQuestion.id) return undefined;

  let surveyJsElement = {
    name: citrusQuestion.id,
    type: questionType.surveyJsType,
    isRequired: citrusQuestion.answerRequired || false,
    fromQuestionBank: citrusQuestion.fromQuestionBank || false,
    title: citrusQuestion.title || citrusQuestion.question,
    placeholder: citrusQuestion.questionPlaceholder
   };

  if (citrusQuestion.typeId === QUESTION_TYPE.textField && citrusQuestion.multiLine) {
    surveyJsElement.type = 'comment';
  }
  else if (citrusQuestion.typeId === QUESTION_TYPE.paragraph) {
    surveyJsElement.title = citrusQuestion.content;
    surveyJsElement.titleLocation = "hidden";
  }
  else if (citrusQuestion.typeId === QUESTION_TYPE.title) {
    surveyJsElement.title = citrusQuestion.includeSectionHeading ? citrusQuestion.title : '';
    surveyJsElement.titleLocation = "hidden";
  }
  else if (citrusQuestion.typeId === QUESTION_TYPE.video) {
    surveyJsElement.videoId = citrusQuestion.videoId;
    surveyJsElement.token = citrusQuestion.token || dummyVideoToken;
    surveyJsElement.titleLocation = "hidden";
    surveyJsElement.videoInsertMethod = citrusQuestion.videoInsertMethod || dummyVideoInsertionMethod;
  }

  // check for the visibleIf attribute and add+convert if necessary
  if (!isNullOrEmptyString(citrusQuestion.visibleIf)) {
    surveyJsElement.visibleIf = citrusQuestion.visibleIf.replace(/\"otherOption\"/g, '"other"');
  }

  // check for stray guids and clear out from titles/content
  surveyJsElement.title = isGuid(surveyJsElement.title) ? "" : decodeText(surveyJsElement.title);
  surveyJsElement.content = isGuid(surveyJsElement.content) ? "" : decodeText(surveyJsElement.content);

  // convert options to choices, handle ratings differently
  if (questionTypeHasOptions(citrusQuestion.typeId)) {
    if (citrusQuestion.options && citrusQuestion.options.length > 0) {

      if (citrusQuestion.typeId === QUESTION_TYPE.ratingScale) {
        const rateValues = citrusQuestion.options.map((x, index) => {
          const rateValue = x.id || x.value || `${index + 1}`;
          return {value: rateValue, text: decodeText(x.value)};
         });
        surveyJsElement.rateValues = rateValues;
        surveyJsElement.minRateDescription = decodeText(citrusQuestion.minimumRateLabel);
        surveyJsElement.maxRateDescription = decodeText(citrusQuestion.maximumRateLabel);
      }
      else {
        const choices = citrusQuestion.options.map(x => { return {value: x.id, text: decodeText(x.value)} });
        surveyJsElement.choices = choices;

        if (citrusQuestion.otherQuestion && citrusQuestion.otherQuestion.label !== '' && citrusQuestion.otherQuestion.enabled === true) {
          surveyJsElement.hasOther = true;

          if (citrusQuestion.otherQuestion.label !== '' && citrusQuestion.otherQuestion.label !== otherDefaultCaption) {
            surveyJsElement.otherText = citrusQuestion.otherQuestion.label;
          }
        }

        if (citrusQuestion.displayOrder) {
          let sortType = citrusSurveyJsSortOrderMappings.find(x => x.citrusSort === citrusQuestion.displayOrder.value);
          if (sortType) {
            surveyJsElement.choicesOrder = sortType.surveyJsSort;
          }
        }

        if (citrusQuestion.displayFormat) {
          surveyJsElement.colCount = citrusQuestion.displayFormat;
        }

        if (citrusQuestion.placeholder) {
          surveyJsElement.optionsCaption = citrusQuestion.placeholder;
        }
      }
    }
  }

  if (citrusQuestion.typeId === QUESTION_TYPE.starRating) {
    let starItems = [];
    for (var i=0; i<citrusQuestion.starCount; i++) {
      starItems.push({value: `${i + 1}`, text: `${i + 1} ${(i === 0) ? 'Star' : 'Stars'}`});
    }
    surveyJsElement.choices = starItems;
  }
  else if (citrusQuestion.typeId === QUESTION_TYPE.constantSum) {
    surveyJsElement.targetSum = citrusQuestion.enforceTargetSum ? (!isNumberNullOrNaN(citrusQuestion.targetSumValue) ? parseInt(citrusQuestion.targetSumValue) : undefined) : undefined;
    surveyJsElement.minValue = !isNumberNullOrNaN(citrusQuestion.minimumValue) ? parseInt(citrusQuestion.minimumValue) : 0;
    surveyJsElement.maxValue = !isNumberNullOrNaN(citrusQuestion.maximumValue) ? parseInt(citrusQuestion.maximumValue) : surveyJsElement.targetSum;
  }

  // Add comment fields only if allowComments is present and true
  if(questionHasComments(citrusQuestion.typeId, citrusQuestion))
  {
    surveyJsElement.allowComments = citrusQuestion.allowComments;
    surveyJsElement.commentsPlaceholder = decodeText(citrusQuestion.commentsPlaceholder);
  }

  return surveyJsElement;
};

export const convertSurveyjsToCitrusJsonConfiguration = (surveyjsJson) => {
  let srcJson = (typeof surveyjsJson == 'string') ? JSON.parse(surveyjsJson) : surveyjsJson;
  if (!srcJson) return undefined;
  //console.log(srcJson);

  let questions = srcJson.pages.map(page => {
    if (page.elements) {
      const pageId = page.name || uuidv4();
      return page.elements.map(element => {return convertSurveyjsToCitrusJsonQuestion(element, pageId)});
    }
  });

  // filter out any null items!
  questions = questions.flat().filter(item => !!item);

  let outputJson = questions;
  //console.log(outputJson);
  return outputJson;
};

export const convertSurveyjsToCitrusJsonQuestion = (surveyJsElement, pageId) => {
  if (!surveyJsElement) return undefined;

  let questionType = citrusSurveyJsQuestionTypeMappings.find(x => x.surveyJsType === surveyJsElement.type);
  if (!questionType) return undefined;

  // set the common props
  let citrusQuestion = {
    id: surveyJsElement.name || uuidv4(),
    key: surveyJsElement.name || uuidv4(),
    pageId: pageId,
    typeId: questionType.citrusQuestionTypeId,
    answerRequired: surveyJsElement.isRequired || false,
    fromQuestionBank: surveyJsElement.fromQuestionBank || false,
    options: [],
    question: surveyJsElement.title || surveyJsElement.placeholder || surveyJsElement.name,
    questionPlaceholder: surveyJsElement.placeholder
   };

  // check for stray guids from the name/title
  citrusQuestion.question = isGuid(citrusQuestion.question) ? "" : citrusQuestion.question;

  // set any custom question-specific props that citrus requires
  if (surveyJsElement.type === 'comment') {
    citrusQuestion.multiLine = true;
  }
  else if (surveyJsElement.type === 'sectionheader') {
    citrusQuestion.title = citrusQuestion.question;
    citrusQuestion.includeSectionHeading = !isNullOrEmptyString(citrusQuestion.title);
  }
  else if (surveyJsElement.type === 'paragraph') {
    citrusQuestion.content = citrusQuestion.question;
  }
  else if (surveyJsElement.type === 'video') {
    citrusQuestion.title = surveyJsElement.title || '';
    citrusQuestion.videoId = surveyJsElement.videoId;
    citrusQuestion.token = surveyJsElement.token;
    citrusQuestion.videoInsertMethod = surveyJsElement.videoInsertMethod;
  }
  else if (surveyJsElement.type === 'dropdown') {
    citrusQuestion.placeholder = surveyJsElement.optionsCaption || "Select...";
    citrusQuestion.questionPlaceholder = citrusQuestion.placeholder;
  }

  // check for the visibleIf attribute and add+convert if necessary
  if (!isNullOrEmptyString(surveyJsElement.visibleIf)) {
    citrusQuestion.visibleIf = surveyJsElement.visibleIf.replace(/\"other\"/g, '"otherOption"');
  }

  // convert choices and rateValues to options?
  if (questionTypeHasOptions(citrusQuestion.typeId)) {
    if ((surveyJsElement.choices && surveyJsElement.choices.length > 0) ||
        (surveyJsElement.items && surveyJsElement.items.length > 0) ||
        (surveyJsElement.rateValues && surveyJsElement.rateValues.length > 0)) {

      if (citrusQuestion.typeId === QUESTION_TYPE.ratingScale) {
        const options = surveyJsElement.rateValues.map(x => { return {id: x.value || x || uuidv4(), value: x.text || x || ''} });
        citrusQuestion.options = options;
        citrusQuestion.minimumRateLabel = surveyJsElement.minRateDescription;
        citrusQuestion.maximumRateLabel = surveyJsElement.maxRateDescription;
      }
      else if (citrusQuestion.typeId === QUESTION_TYPE.multipleText && (surveyJsElement.items && surveyJsElement.items.length > 0)) {
        const options = surveyJsElement.items.map(x => { return {id: x.name || uuidv4(), value: x.title || ''} });
        citrusQuestion.options = options;
      }
      else {
        const options = surveyJsElement.choices.map(x => { return {id: x.value || uuidv4(), value: x.text || ''} });
        citrusQuestion.options = options;
        citrusQuestion.otherQuestion = {};

        if (surveyJsElement.hasOther) {
          citrusQuestion.otherQuestion = {
            value: '',
            label: (!isNullOrEmptyString(surveyJsElement.otherText)) ? surveyJsElement.otherText || otherDefaultCaption : otherDefaultCaption,
            enabled: true
          }
        }

        if (surveyJsElement.choicesOrder) {
          let sortType = citrusSurveyJsSortOrderMappings.find(x => x.surveyJsSort === surveyJsElement.choicesOrder);
          if (sortType) {
            citrusQuestion.displayOrder = {value: sortType.citrusSort, label: sortType.citrusLabel};
          }
        }

        if (surveyJsElement.colCount) {
          citrusQuestion.displayFormat = surveyJsElement.colCount;
        }

        if (surveyJsElement.optionsCaption) {
          citrusQuestion.placeholder = surveyJsElement.optionsCaption;
        }
      }
    }
  }

  if (citrusQuestion.typeId === QUESTION_TYPE.starRating) {
    citrusQuestion.starCount = surveyJsElement.choices ? surveyJsElement.choices.length : 5;
  }
  else if (citrusQuestion.typeId === QUESTION_TYPE.constantSum) {
    citrusQuestion.enforceTargetSum = !isNumberNullOrNaN(surveyJsElement.targetSum) && parseInt(surveyJsElement.targetSum) > 0;
    citrusQuestion.targetSumValue = !isNumberNullOrNaN(surveyJsElement.targetSum) ? parseInt(surveyJsElement.targetSum) : undefined;
    citrusQuestion.minimumValue = !isNumberNullOrNaN(surveyJsElement.minValue) ? parseInt(surveyJsElement.minValue) : 0;
    citrusQuestion.maximumValue = !isNumberNullOrNaN(surveyJsElement.maxValue) ? parseInt(surveyJsElement.maxValue) : 0;
  }

  // Add allowComments only if present and true
  if(questionHasComments(citrusQuestion.typeId, surveyJsElement))
  {
    citrusQuestion.allowComments = surveyJsElement.allowComments;
  }

  // Required as default for proper user experience with default comment placeholder value
  citrusQuestion.commentsPlaceholder = surveyJsElement.commentsPlaceholder || 'Enter comments';

  return citrusQuestion;
};

export const convertCitrusResponsesSurveyjsJsonAnswers = (citrusResponses) => {
  let answers = {};
  if (!citrusResponses || citrusResponses.length <= 0) return answers;

  // eval question/response typeId to process responses
  citrusResponses.map(item => {
    if (!item.response) return;

    if (Array.isArray(item.response)) {
      if (item.response.length > 0) {
        // for checkboxes/stackrank, create an array with just the optionId's
        if (questionTypeHasMultipleKeys(item.typeId)) {
          let otherItemText = '';
          const selected = item.response.map(x => {
            // 'otherOption' was selected, so return the survyejs 'other' item and set the comment
            if (x.optionId === 'otherOption') {
              otherItemText = x.value;
              return 'other'
            }
            else if (x.optionId === 'other') {
              // do nothing! cleanout any leftovers!
              return;
            }
            else {
              return x.optionId;
            }
          });

          // remove any null/undefined items from the list
          answers[item.id] = selected.filter((sel) => { return sel !== null && sel !== undefined; });

          // if the otherText was set, save it in surveyjs format
          if (otherItemText.length > 0) {
            answers[`${item.id}-Comment`] = decodeText(otherItemText);
          }
        }
        // for constantSum and multipleText, create an array with elements of {optionId: value}'s
        else if (questionTypeHasMultipleKeyValues(item.typeId)) {
          let selected = {};
          item.response.map(x => { selected[x.optionId] = decodeText(x.value); });
          answers[item.id] = selected;
        }
        // for all other selection-type answers, just set the answer to the selected optionId
        else {
          if (!isNullOrEmptyString(item.response[0].optionId)) {
            // 'otherOption' was selected, so return the survyejs 'other' item and set the comment
            if (item.response[0].optionId === 'otherOption') {
              answers[item.id] = 'other';
              answers[`${item.id}-Comment`] = decodeText(item.response[0].value) || '';
            }
            else {
              answers[item.id] = item.response[0].optionId;
            }
          }
        }
      }
    }
    else {
      // for non-selection type questions, just the answer to the response
      answers[item.id] = typeof item.response === 'string' ? decodeText(item.response) : `${item.response}`;
    }

    if(item.comment && item.comment.length > 0) {
      let userComment = item.comment || '';
      answers[`${item.id}-UserComment`] = decodeText(userComment);
    }

  });

  return answers;
};

export const applySavedOtherTextToQuestions = (citrusQuestions, citrusResponses) => {
  if (!citrusQuestions || !citrusResponses || citrusResponses.length === 0) return citrusQuestions;

  return citrusQuestions.map(question => {
    if (!question || question.typeId !== QUESTION_TYPE.checkbox) return question;
    if (!question.otherQuestion || !question.otherQuestion.enabled) return question;

    const questionResponse = citrusResponses.find(x => x.id === question.id);
    const otherResponse = (questionResponse && Array.isArray(questionResponse.response))
      ? questionResponse.response.find(x => x.optionId === 'otherOption')
      : undefined;
    if (!otherResponse || isNullOrEmptyString(otherResponse.value)) return question;

    return { ...question, otherQuestion: { ...question.otherQuestion, value: otherResponse.value } };
  });
};

// Ticking "Other" after other options no longer unticks the one ticked last: the Citrus checkbox removes it from the
// response when Other is added, so it is put back here.
export const keepSelectionsWhenOtherIsTicked = (previousResponse, nextResponse) => {
  if (!nextResponse || nextResponse.typeId !== QUESTION_TYPE.checkbox || !Array.isArray(nextResponse.response)) return nextResponse;

  const before = (previousResponse && Array.isArray(previousResponse.response)) ? previousResponse.response : [];
  const other = nextResponse.response.find(x => x.optionId === 'otherOption');
  if (!other || before.some(x => x.optionId === 'otherOption')) return nextResponse; // only the moment Other is ticked

  const kept = nextResponse.response.filter(x => x.optionId !== 'otherOption');
  const dropped = before.filter(x => !kept.some(k => k.optionId === x.optionId));
  return dropped.length === 0 ? nextResponse : { ...nextResponse, response: [...before, other] };
};

export const convertSurveyjsJsonAnswersToCitrusResponses = (surveyAnswers, citrusJsonData) => {
  let responses = [];
  let surveyAnswerJson = (typeof surveyAnswers == 'string') ? JSON.parse(surveyAnswers) : surveyAnswers;

  if (!surveyAnswerJson || JSON.stringify(surveyAnswerJson) === "{}") return responses;
  if (!citrusJsonData || JSON.stringify(citrusJsonData) === "{}") return responses;

  // flatten the citrus questions to get rid of pages
  citrusJsonData = citrusJsonData.flat();

  // need to grab the typeId for each surveyJs element to compose citrus answers correctly!
  for (var key in surveyAnswerJson) {
    // find the citrus question
    const citrusQuestion = citrusJsonData.find(x => x.id === key);

    if (surveyAnswerJson.hasOwnProperty(key) && citrusQuestion) {
      let response = null;
      if (questionTypeHasOptions(citrusQuestion.typeId)) {
        // ratingsScale has options but is a simple value
        if (questionTypeHasMultipleKeyValues(citrusQuestion.typeId)) {
          let answers = [];
          for (var itemkey in surveyAnswerJson[key]) {
            let answer = { optionId: itemkey, value: surveyAnswerJson[key][itemkey] };
            answers.push(answer);
          }

          // save the answer(s) to the response object
          response = {id: key, typeId: citrusQuestion.typeId, response: answers};
        }
        else {
          let answers = null;

          if (Array.isArray(surveyAnswerJson[key])) {
            answers = surveyAnswerJson[key].map(item => {
              if (item === 'other') {
                let otherText = surveyAnswerJson[`${key}-Comment`] || '';
                return { optionId: 'otherOption', value: otherText };
              }
              else {
                return { optionId: item };
              }
            });
          }
          else {
            if (surveyAnswerJson[key] === 'other') {
              let otherText = surveyAnswerJson[`${key}-Comment`] || '';
              answers = [{ optionId: 'otherOption', value: otherText }];
            }
            else {
              answers = [{ optionId: surveyAnswerJson[key] }];
            }
          }

          // save the answer(s) to the response object
          response = {id: key, typeId: citrusQuestion.typeId, response: answers};
        }
      }
      else {
        let answer = (questionTypeIsStarRatings(citrusQuestion.typeId) && surveyAnswerJson[key] && surveyAnswerJson[key].length > 0) ? parseInt(surveyAnswerJson[key]) : surveyAnswerJson[key];
        response = {id: key, typeId: citrusQuestion.typeId, response: answer};
      }

      // If user comment exists for key, add comment to response object
      let userComment = surveyAnswerJson[`${key}-UserComment`] || '';
      if(userComment.length > 0) {
        response = {...response, comment: userComment};
      }

      // add the response object to our array
      responses.push(response);
    }
  }

  return responses;
};
